import { Injectable } from "@angular/core";
import { LocalStorageService } from "core-kit";
import {
  CfsTemplateError,
  CfsTemplateErrorDto,
  CfsTemplateErrorGroup,
  CfsTemplateErrorOccurrence,
  CfsTemplateSkipInfo,
  Template
} from "clearline-common";
import { StorageKeys } from "@app-cfs/shared/models/storage-keys";
import { BehaviorSubject } from "rxjs";
import { ConsoleLogHandlerService } from "@app-cfs/shared/services/console-log-handler.service";
import { ActivityLogService } from "@app-cfs/shared/services/activity-log.service";
import { SettingsHandlerService } from "@app-cfs/shared/services/settings-handler.service";
import { TemplateActivityPostDto, TemplateActivityType } from "@app-cfs/shared/models";
import { GlobalTemplateType } from "clearline-api";
import { take } from "rxjs/operators";

@Injectable({
  providedIn: "root"
})
export class CarouselSkipSlideService {
  private _isAllSlidesSkipped = false;
  private slideSkipListSrc$ = new BehaviorSubject<number[]>([]);

  private readonly parallelErrorThreshold = 1000;
  private readonly errorRetentionDays = 14;
  /**
   * Observable for the list of skipped slide template IDs.
   * This is used to track which slides have been skipped in the carousel.
   */
  readonly slideSkipList$ = this.slideSkipListSrc$.asObservable();

  get skipList(): number[] {
    return this.slideSkipListSrc$.value;
  }

  get isAllSlidesSkipped(): boolean {
    return this._isAllSlidesSkipped;
  }

  constructor(
    private localStorageService: LocalStorageService,
    private logService: ConsoleLogHandlerService,
    private activityLogService: ActivityLogService,
    private settingsHandler: SettingsHandlerService
  ) {}

  isSlideShouldBeSkipped(templateId: number): boolean {
    return this.slideSkipListSrc$.value.includes(templateId);
  }

  /**
   * Runs every time a slide starts successfully displaying.
   * This method updates the stored skip information for the slide.
   * @param template The slide template
   */
  setSlideSuccessDisplay(template: Template): void {
    if (!template?.templateId) {
      return;
    }

    const { templateId } = template;
    const skipInfoList: CfsTemplateSkipInfo[] = this.getStoredSlidesSkipInfo();
    const storedSlideInfo: CfsTemplateSkipInfo | null = this.getStoredSlideInfo(skipInfoList, templateId);

    const hasResetUpdates: boolean = this.resetOutdatedErrorFlags(storedSlideInfo?.errorGroups);

    if (hasResetUpdates) {
      this.storeSlidesSkipInfo(skipInfoList);
    }
  }

  /**
   * Marks a slide as having an error and updates the stored error information
   *
   * @param template The slide template that had an error
   * @param index The index of the slide template
   * @param error The error details
   */
  setSlideError(template: Template, index: number, error: CfsTemplateError): void {
    if (!template || !error) {
      return;
    }

    this.logSlideError(template, error);

    const { templateId, name = "" } = template;
    const skipInfoList: CfsTemplateSkipInfo[] = this.getStoredSlidesSkipInfo();
    const cleanedList: CfsTemplateSkipInfo[] = this.cleanupOldErrors(skipInfoList);
    const storedSlideInfo: CfsTemplateSkipInfo | null = this.getStoredSlideInfo(cleanedList, templateId);
    const errorDate = error.date || new Date().toISOString();
    const errorOccurrence: CfsTemplateErrorOccurrence = {
      date: errorDate,
      isActualError: true,
      slideIndex: index
    };

    if (storedSlideInfo) {
      this.updateExistingTemplateInfo(storedSlideInfo, error, errorOccurrence);
    } else {
      const templateSkipInfo: CfsTemplateSkipInfo = {
        templateId,
        templateName: name,
        errorGroups: [this.createErrorGroup(error, errorOccurrence)]
      };

      cleanedList.push(templateSkipInfo);
    }

    this.storeSlidesSkipInfo(cleanedList);
    this.setSkipTemplateId(templateId);
  }

  setIsAllTemplatesSkipped(isAllSkipped: boolean): void {
    if (this._isAllSlidesSkipped !== isAllSkipped) {
      this._isAllSlidesSkipped = isAllSkipped;
    }
  }

  updateAllTemplatesSkippedState(slideList: Template[] = []): void {
    if (!this.isAllSlidesSkipped && this.calculateIsAllSlidesSkipped(slideList)) {
      this.setIsAllTemplatesSkipped(true);
    }
  }

  clearData(): void {
    this.slideSkipListSrc$.next([]);
  }

  private logSlideError(template: Template, error: CfsTemplateError): void {
    if (!template || !error) {
      return;
    }

    const { templateId, globalTemplateType } = template;
    const { locationId, cashRegister } = this.settingsHandler;
    const errorDate = error.date || new Date().toISOString();
    const errorDto: CfsTemplateErrorDto = {
      message: error.message,
      type: error.type
    };
    const activityDto: TemplateActivityPostDto = {
      templateId,
      isGlobalTemplate: globalTemplateType === GlobalTemplateType.Global,
      activityType: TemplateActivityType.ShowBlocked,
      locationId: locationId,
      cashRegisterId: cashRegister,
      dateFrom: errorDate,
      dateTo: errorDate,
      count: 1,
      data: JSON.stringify(errorDto)
    };

    this.activityLogService.logTemplateActivity(activityDto).pipe(take(1)).subscribe();
  }

  private updateExistingTemplateInfo(
    slideInfo: CfsTemplateSkipInfo,
    error: CfsTemplateError,
    newOccurrence: CfsTemplateErrorOccurrence
  ): void {
    const errorGroups: CfsTemplateErrorGroup[] | null = slideInfo.errorGroups || null;

    if (errorGroups) {
      this.resetOutdatedErrorFlags(errorGroups, error.date);

      const existingGroup: CfsTemplateErrorGroup | null = this.findMatchingErrorGroup(errorGroups, error);

      if (existingGroup) {
        existingGroup.occurrences.push(newOccurrence);
      } else {
        this.resetOutdatedErrorFlags(errorGroups, error.date);

        errorGroups.push(this.createErrorGroup(error, newOccurrence));
      }
    } else {
      slideInfo.errorGroups = [];
    }
  }

  private createErrorGroup(error: CfsTemplateError, occurrence: CfsTemplateErrorOccurrence): CfsTemplateErrorGroup {
    return {
      error: {
        type: error.type,
        message: error.message,
        data: error.data
      },
      occurrences: [occurrence]
    };
  }

  private resetOutdatedErrorFlags(errorGroups?: CfsTemplateErrorGroup[], actualErrorDate: string = ""): boolean {
    if (!errorGroups?.length) {
      return false;
    }

    let hasUpdates = false;
    const actualErrorTime = actualErrorDate ? new Date(actualErrorDate).getTime() : Date.now();

    for (const group of errorGroups) {
      for (const occurrence of group.occurrences) {
        if (occurrence.isActualError) {
          const occurrenceTime = new Date(occurrence.date).getTime();

          if (Math.abs(actualErrorTime - occurrenceTime) > this.parallelErrorThreshold) {
            occurrence.isActualError = false;
            hasUpdates = true;
          }
        }
      }
    }

    return hasUpdates;
  }

  private findMatchingErrorGroup(errorGroups: CfsTemplateErrorGroup[], error: CfsTemplateError): CfsTemplateErrorGroup | null {
    const { type = "", message = "", data = "" } = error || {};

    if (!errorGroups?.length || !error || !message) {
      return null;
    }

    return (
      errorGroups.find(
        (group: CfsTemplateErrorGroup) =>
          group.error.type === type && group.error.message === message && JSON.stringify(group.error.data) === JSON.stringify(data)
      ) || null
    );
  }

  private setSkipTemplateId(templateId: number): void {
    const slideSkipList: number[] = this.slideSkipListSrc$.value;

    if (!slideSkipList.includes(templateId)) {
      this.slideSkipListSrc$.next([...slideSkipList, templateId]);
      this.logService.log(`skip template ${templateId}, templateIdSkipList: `, this.slideSkipListSrc$.value);
    }
  }

  private calculateIsAllSlidesSkipped(templateList: Template[] = []): boolean {
    return templateList?.every((template: Template) => {
      return template.isSplashScreen || this.isSlideShouldBeSkipped(template.templateId);
    });
  }

  private getStoredSlideInfo(storedList: CfsTemplateSkipInfo[], currentSlideId: number): CfsTemplateSkipInfo | null {
    return storedList.find((item: CfsTemplateSkipInfo) => item.templateId === currentSlideId) || null;
  }

  private getStoredSlidesSkipInfo(): CfsTemplateSkipInfo[] {
    const result: string | null = this.localStorageService.getItem(StorageKeys.slidesSkipInfo);

    return result ? JSON.parse(result) : [];
  }

  private storeSlidesSkipInfo(slidesSkipInfo: CfsTemplateSkipInfo[] = []): void {
    this.localStorageService.setItem(StorageKeys.slidesSkipInfo, JSON.stringify(slidesSkipInfo));
  }

  private cleanupOldErrors(skipInfoList: CfsTemplateSkipInfo[] | null = null): CfsTemplateSkipInfo[] {
    if (!skipInfoList?.length) {
      return [];
    }

    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - this.errorRetentionDays);
    const twoWeeksAgoTime = twoWeeksAgo.getTime();

    const result: CfsTemplateSkipInfo[] = [];

    for (const info of skipInfoList) {
      if (!info.errorGroups?.length) {
        continue;
      }

      const validGroups: CfsTemplateErrorGroup[] = [];

      for (const group of info.errorGroups) {
        const validOccurrences: CfsTemplateErrorOccurrence[] = [];

        for (const occ of group.occurrences) {
          if (occ.isActualError || new Date(occ.date).getTime() > twoWeeksAgoTime) {
            validOccurrences.push(occ);
          }
        }

        if (validOccurrences.length) {
          validGroups.push({ ...group, occurrences: validOccurrences });
        }
      }

      if (validGroups.length) {
        result.push({ ...info, errorGroups: validGroups });
      }
    }

    return result;
  }
}
